/**
 * shopifyCart.js
 * --------------
 * Thin, shared bridge to the Shopify Buy SDK cart that already powers the
 * Featured Collectibles embed on /shop (see `ShopifyBuyButton.jsx`).
 *
 * The rules for CollectorUp remain unchanged:
 *   - Shopify is the SINGLE source of truth for commerce (variants,
 *     prices, inventory, cart, checkout, taxes, shipping).
 *   - CollectorUp NEVER duplicates prices, inventory, or line item math.
 *   - "Add to Cart" from any CollectorUp card must call into Shopify's
 *     Buy SDK cart on the same page — no external redirects.
 *
 * This module lazily loads the Shopify Buy SDK, builds a client with
 * the same storefront domain + token used by the Featured Collectibles
 * embed, and exposes a single async helper — `addVariantToCart(variantId, qty)` —
 * that adds a line item to the shared cart component and opens the
 * cart drawer. If a cart component already exists (created by the
 * Featured embed), we reuse it so there is exactly ONE cart per page.
 *
 * The featured embed hard-codes the same domain / token below; keeping
 * them in one place lets us swap credentials or shift them into env
 * variables in a single follow-up commit without touching card code.
 */

const SHOPIFY_DOMAIN = "hvn653-ee.myshopify.com";
const SHOPIFY_STOREFRONT_TOKEN = "b327df1ea4faa63e2e12bed8c6f090ed";
const SDK_URL =
  "https://sdks.shopifycdn.com/buy-button/latest/buy-button-storefront.min.js";

let sdkPromise = null;
let uiPromise = null;

function loadSdk() {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("shopifyCart: window unavailable (SSR)"));
  }
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    if (window.ShopifyBuy && window.ShopifyBuy.UI) return resolve(window.ShopifyBuy);

    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.ShopifyBuy));
      existing.addEventListener("error", reject);
      return;
    }

    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.ShopifyBuy);
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return sdkPromise;
}

function getUi() {
  if (uiPromise) return uiPromise;
  uiPromise = loadSdk().then((ShopifyBuy) => {
    const client = ShopifyBuy.buildClient({
      domain: SHOPIFY_DOMAIN,
      storefrontAccessToken: SHOPIFY_STOREFRONT_TOKEN,
    });
    return ShopifyBuy.UI.onReady(client);
  });
  return uiPromise;
}

/**
 * Add a Shopify variant to the shared in-page cart and open the drawer.
 *
 * @param {string|number} variantId  Shopify variant id (or GID).
 * @param {number} [quantity=1]      Quantity to add.
 * @returns {Promise<{ok: boolean, reason?: string, error?: any}>}
 *
 * Reuses the cart component created by the Featured Collectibles embed
 * when present so there is exactly one cart drawer per page. Falls back
 * to creating a standalone cart component if no featured embed is
 * mounted (e.g. on future dedicated Team Set catalog pages).
 */
export async function addVariantToCart(variantId, quantity = 1) {
  if (!variantId) return { ok: false, reason: "missing-variant-id" };
  try {
    const ui = await getUi();

    let cart =
      ui.components && ui.components.cart && ui.components.cart[0]
        ? ui.components.cart[0]
        : null;

    if (!cart) {
      const host = document.createElement("div");
      host.setAttribute("data-shopify-cart-host", "team-set-card");
      document.body.appendChild(host);
      cart = await ui.createComponent("cart", {
        node: host,
        options: {
          cart: { text: { total: "Subtotal", button: "Checkout" } },
        },
      });
    }

    await cart.addVariantToCart({ id: variantId, quantity });
    if (typeof cart.open === "function") cart.open();
    return { ok: true };
  } catch (error) {
    /* Surface a structured failure so cards can toast without crashing. */
    // eslint-disable-next-line no-console
    console.error("shopifyCart.addVariantToCart failed", error);
    return { ok: false, reason: "sdk-error", error };
  }
}
